import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { useQuerystring } from "$routes/_lib/use-querystring";
import { gql } from "@apollo/client";
import type {
  QueryPullReferralArgs,
  ReferralSyncPushInput,
} from "elephant-web/gql";
import { useMemo } from "react";
import { useMatch } from "react-router-dom";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { Referral } from "../collections/referral";
import type { RxDBCollections } from "../provider";
import {
  SyncReferralPullDocument,
  SyncReferralPushDocument,
  type WholeSyncableReferralFragment,
} from "./__generated__/referral.gql";
import { createPersistedCheckpointPullBuilders } from "./create-persisted-checkpoint-pull-builders";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

export const useReplicateReferral = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<Referral>;
}) => {
  const query = useQuerystring();
  const matchPat = useMatch("/dev-patient/:patientId/*");
  const patientId = matchPat?.params.patientId ?? query.patientUuid;
  const [featureFlagEnabled] = useFeatureFlag(featureFlags.pocus.referralsSync);

  const push = useMemo(
    () => ({
      mutation: SyncReferralPushDocument,
      mapRxToGql: mapRxReferralToGqlReferralInput,
    }),
    [],
  );

  const pull = useMemo<
    ReplicationPullConfig<WholeSyncableReferralFragment> | undefined
  >(() => {
    return featureFlagEnabled && patientId
      ? {
          query: SyncReferralPullDocument,
          reSyncInterval: { minutes: 5 },
          ...createPersistedCheckpointPullBuilders<
            QueryPullReferralArgs,
            WholeSyncableReferralFragment
          >({
            buildInputVariables: (checkpoint, batchSize) => ({
              input: {
                limit: batchSize,
                filters: { patientId },
                checkpoint,
              },
            }),
            buildPullCheckpoint: (doc) => ({
              id: doc.id,
              updatedAt: doc.meta.updatedAt,
            }),
            entityId: patientId,
            db,
            collectionName: collection?.name,
          }),
        }
      : undefined;
  }, [featureFlagEnabled, patientId, db, collection]);

  return useGqlReplication<
    Referral,
    WholeSyncableReferralFragment,
    ReferralSyncPushInput
  >({
    db,
    collection,
    push: featureFlagEnabled && patientId ? push : undefined,
    pull,
    mapGqlToRx: mapGqlReferralToRxReferral,
    batchSize: 100,
    expireData: true,
    metadataLocalStorageKeySuffix: `referral-${patientId}`,
    enabled: featureFlagEnabled && !!patientId,
  });
};

export const mapGqlReferralToRxReferral = (
  referral: WholeSyncableReferralFragment,
): WithDeleted<Referral> => ({
  id: referral.id,
  facilityId: referral.facilityId || null,
  freeTextFacility: referral.freeTextFacility || null,
  reasons: [...referral.reasons],
  notes: referral.notes || null,
  patientId: referral.patientId,
  meta: {
    createdAt: referral.meta.createdAt,
    updatedAt: referral.meta.updatedAt,
    createdByUserId: referral.meta.createdByUserId,
    updatedByUserId: referral.meta.updatedByUserId,
  },
  _deleted: referral.meta.deleted,
});

export const mapRxReferralToGqlReferralInput = (
  referral: WithDeleted<Referral>,
): ReferralSyncPushInput => ({
  id: referral.id,
  facilityId: referral.facilityId,
  freeTextFacility: referral.freeTextFacility,
  reasons: referral.reasons,
  notes: referral.notes,
  patientId: referral.patientId,
  meta: {
    createdAt: referral.meta.createdAt,
    updatedAt: referral.meta.updatedAt,
    createdByUserId: referral.meta.createdByUserId,
    updatedByUserId: referral.meta.updatedByUserId,
    deleted: referral._deleted,
  },
});

gql`
  fragment WholeSyncableReferral on SyncableReferral {
    id
    facilityId
    freeTextFacility
    reasons
    notes
    patientId
    meta {
      createdAt
      updatedAt
      createdByUserId
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncReferralPull($input: PullReferralInput!) {
    pullReferral(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncableReferral
      }
    }
  }
`;

gql`
  mutation SyncReferralPush($input: PushReferralInput!) {
    pushReferral(input: $input) {
      rows {
        ...WholeSyncableReferral
      }
    }
  }
`;
